"use client";
import { useEffect, useId, useState } from "react";
import getEmployeesForService from "@/dataLayer/getEmployeesForService";
import { ServiceModalEmployeeSelection } from "./serviceModalEmployeeSelection";
import { ServiceModalModifierSelection } from "./serviceModalModifierSelection";
import { v4 as uuidv4 } from "uuid";
import { ServiceCart } from "@/models/cart/serviceCart";
import { ClearCartModal } from "@/components/clearCartModal/clearCartModal";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import { RetailCartItem } from "@/models/cart/types";
import { GiftCard } from "@/dto/giftCard";
import { removeFromSessionStorage } from "@/utils/sessionStorageUtils";
import { LocalStorageKey, SessionStorageKey } from "@/utils/storageKeys";
import { RegularModal } from "@/components/modals/modals";
import { getMatchingModifiers } from "@/utils/modifierUtils";
import { ModifierGroup } from "@/dto/modifierGroup";
import { Modifier } from "@/dto/modifier";
import { useTranslations } from "next-intl";
import { useFormatDuration } from "@/utils/useFormatDuration";
import { CartItemType } from "@/constants/enum";
import { DEFAULT_ID } from "@/utils/constants";
import { useScrollObserver } from "@/utils/useScrollObserver";
import { ImageModalCloseButton } from "@/components/_icons/imageModalCloseButton";
import CloseIcon from "@/components/_icons/closeIcon";
import clsx from "clsx";

export const ServiceModal = ({
  merchantName,
  isCheckoutEnabled,
  isOpen,
  onRequestClose,
  service,
  modifiers,
  merchantId,
  menu,
  isAcceptingAppointments,
  merchantPreferences,
  merchantColor,
}: any) => {
  const tServiceModal = useTranslations("ServiceModal");
  const formatDuration = useFormatDuration();

  const [showEmployeeSelection, setShowEmployeeSelection] =
    useState<boolean>(false);
  const [employeeList, setEmployeeList] = useState<any[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<string>("");
  const [selectedModifiers, setSelectedModifiers] = useState<any>({});
  const [quantity, setQuantity] = useState<number>(1);
  const [finalPrice, setFinalPrice] = useState(service.price);
  const [isClearCartOpen, setIsClearCartOpen] = useState<boolean>(false);
  const [isAddtocartClicked, setIsAddtocartClicked] = useState<boolean>(false);
  const titleId = useId();
  const subtitleId = useId();
  const {
    containerRef,
    targetRef: imageRef,
    isIntersecting: isImageInView,
  } = useScrollObserver(0);

  useEffect(() => {
    if (selectedEmployee === "default" && employeeList.length === 1) {
      handleAddToServiceCart();
    }
  }, [selectedEmployee, employeeList]);

  const openClearCart = () => {
    setIsClearCartOpen(true);
  };

  const mapModifierGroup = (groupId: string) => {
    const group: ModifierGroup = menu.modifierGroups[groupId];
    const selections = selectedModifiers[groupId];
    return Object.keys(selections).map((modifierId) => {
      const modifiers = getMatchingModifiers({
        modifiers: menu?.modifiers,
        item: service,
        modifierGroups: menu?.modifierGroups,
      });
      const modifier: Modifier | undefined = modifiers.find(
        (m: Modifier) => m.id === modifierId
      );
      return {
        name: modifier?.name,
        id: modifier?.id,
        price: modifier?.price,
        groupId: group?.id,
        quantity: selections[modifierId],
      };
    });
  };

  const processSelectedModifiers = () => {
    if (Object.keys(selectedModifiers).length === 0) return [];

    return Object.keys(selectedModifiers).map(mapModifierGroup).flat();
  };

  const handleAddToServiceCart = () => {
    const selectedEmployeeDetails = employeeList.find(
      (employee: any) => employee.employeeUuid === selectedEmployee
    );

    const retailCart = getFromLocalStorage<RetailCartItem[]>(
      LocalStorageKey.RETAIL_CART,
      []
    );
    const giftCard = getFromLocalStorage<GiftCard | null>(
      LocalStorageKey.GIFT_CARD,
      null
    );

    const finalSelectedModifiers = processSelectedModifiers();

    const newService = {
      type: CartItemType.SERVICE as const,
      id: service.id,
      name: service.name,
      price: service.price,
      quantity: quantity,
      modifiers: finalSelectedModifiers,
      specialInstructions: "",
      subtotal: finalPrice,
      total: finalPrice,
      cartId: uuidv4(),
      employee: selectedEmployeeDetails,
      serviceDetails: service,
    };

    if (retailCart.length >= 1 || giftCard) {
      openClearCart();
    } else {
      ServiceCart.addItem(newService);
      onRequestClose();
    }
  };

  const handleClearCartClose = () => {
    setIsClearCartOpen(false);
  };

  const validateModifierSelection = () => {
    const filteredModifierGroups = menu.modifierGroups
      ? Object.values(menu.modifierGroups).filter((group: any) =>
          service.modifierGroupIds.includes(group.id)
        )
      : [];
    const selectableModifierGroups: ModifierGroup[] =
      filteredModifierGroups.length > 0
        ? filteredModifierGroups
            .map((group: any) => ({
              ...group,
              modifiers: modifiers.filter(
                (mod: Modifier) => mod.groupId === group.id
              ),
              isSingleSelection:
                group.minRequired === 1 && group.maxAllowed === 1,
            }))
            .filter(
              (group: ModifierGroup) =>
                group.modifiers?.length > 0 &&
                group.modifiers?.some(
                  (modifier: Modifier) => modifier.available
                )
            )
        : [];
    return Object.values(selectableModifierGroups || {}).every((group: any) => {
      const groupSelections: { [key: string]: number } =
        selectedModifiers[group.id] ?? {};
      const totalGroupQuantity = Object.values(groupSelections).reduce(
        (acc: number, quantity: number) => acc + quantity,
        0
      );
      return (
        totalGroupQuantity >= group.minRequired &&
        totalGroupQuantity <= group.maxAllowed
      );
    });
  };

  async function onShowEmployeeSelection() {
    if (!validateModifierSelection()) {
      setIsAddtocartClicked(true);
      return;
    }
    removeFromSessionStorage(SessionStorageKey.PICKUP_TIME);
    const serviceEmployees = await getEmployeesForService(
      merchantId,
      service.id
    );
    const defaultEmployee = {
      name: tServiceModal("anyAvailableStaff"),
      employeeUuid: DEFAULT_ID,
      itemUuid: service.id,
    };
    if (
      serviceEmployees?.length != 0 &&
      !merchantPreferences.removeStaffFromSite
    ) {
      if (
        serviceEmployees?.length == 1 ||
        !merchantPreferences.anyAvailableStaff
      ) {
        setEmployeeList(serviceEmployees);
        setSelectedEmployee(serviceEmployees[0].employeeUuid);
        setShowEmployeeSelection(true);
      } else {
        setEmployeeList([defaultEmployee, ...serviceEmployees]);
        setSelectedEmployee(defaultEmployee.employeeUuid);
        setShowEmployeeSelection(true);
      }
    } else {
      setEmployeeList([defaultEmployee]);
      setSelectedEmployee(defaultEmployee.employeeUuid);
    }
  }

  const handleEmployeeSelect = (employeeUuid: string) => {
    setSelectedEmployee(employeeUuid);
  };

  const productImage =
    service.images && service.images.length > 0
      ? service.images.reduce((prev: any, current: any) => {
          if (
            current.name.includes("xxxhdpi") ||
            (current.name.includes("xxhdpi") &&
              !prev.name.includes("xxxhdpi")) ||
            (current.name.includes("xhdpi") &&
              !prev.name.includes("xxhdpi") &&
              !prev.name.includes("xxxhdpi"))
          ) {
            return current;
          }
          return prev;
        }, service.images[0])
      : null;

  return (
    <>
      {isClearCartOpen && (
        <ClearCartModal
          isOpen={isClearCartOpen}
          onRequestClose={handleClearCartClose}
          merchantColor={merchantColor}
          addToCart={handleAddToServiceCart}
        />
      )}
      {!isClearCartOpen && (
        <RegularModal
          isOpen={isOpen}
          onRequestClose={onRequestClose}
          overlayClassName="bg-opacity-0"
          contentClassName={clsx(
            "bg-white sm:rounded-lg shadow-lg w-full mx-auto h-[100%] sm:h-[576px] overflow-hidden",
            productImage ? "sm:max-w-[900px]" : "sm:w-fit sm:max-w-[600px]"
          )}
          closeIcon={
            productImage && isImageInView ? (
              <ImageModalCloseButton />
            ) : (
              <CloseIcon />
            )
          }
          focusElementId={titleId}
          aria={{
            labelledby: titleId,
            describedby: subtitleId,
          }}
        >
          {!showEmployeeSelection && (
            <ServiceModalModifierSelection
              isCheckoutEnabled={isCheckoutEnabled}
              merchantName={merchantName}
              onRequestClose={onRequestClose}
              productImage={productImage}
              service={service}
              formatDuration={formatDuration}
              onShowEmployeeSelection={onShowEmployeeSelection}
              menu={menu}
              modifiers={modifiers}
              selectedModifiers={selectedModifiers}
              setSelectedModifiers={setSelectedModifiers}
              setFinalPrice={setFinalPrice}
              isAcceptingAppointments={isAcceptingAppointments}
              merchantColor={merchantColor}
              quantity={quantity}
              setQuantity={setQuantity}
              isAddtocartClicked={isAddtocartClicked}
              setIsAddtocartClicked={setIsAddtocartClicked}
              imageRef={imageRef}
              scrollContainerRef={containerRef}
              titleId={titleId}
              subtitleId={subtitleId}
            />
          )}
          {showEmployeeSelection && (
            <ServiceModalEmployeeSelection
              productImage={productImage}
              service={service}
              employeeList={employeeList}
              handleEmployeeSelect={handleEmployeeSelect}
              selectedEmployee={selectedEmployee}
              setShowEmployeeSelection={setShowEmployeeSelection}
              handleAddToServiceCart={handleAddToServiceCart}
              finalPrice={finalPrice}
              merchantColor={merchantColor}
              imageRef={imageRef}
              scrollContainerRef={containerRef}
              titleId={titleId}
            />
          )}
        </RegularModal>
      )}
    </>
  );
};
